import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';
import { getRuntimeConfig } from '../services/runtimeConfig';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cas_token'));
  const [loading, setLoading] = useState(true);
  const skipNextLoadUserRef = useRef(false);

  const applyAuthSession = (data) => {
    if (!data?.token || !data?.user) return false;
    localStorage.setItem('cas_token', data.token);
    localStorage.setItem('cas_user', JSON.stringify(data.user));
    skipNextLoadUserRef.current = true;
    setUser(data.user);
    setToken(data.token);
    return true;
  };

  const loadUser = useCallback(async () => {
    if (!token) {
      if (getRuntimeConfig().atozasSsoEnabled) {
        try {
          const data = await authAPI.getAtozasMe();
          if (data?.authenticated !== false && applyAuthSession(data)) {
            sessionStorage.removeItem('cas_atozas_silent');
            setLoading(false);
            return;
          }
        } catch {
          // No local ATOZAS cookie — try silent IdP session next.
        }

        const params = new URLSearchParams(window.location.search);
        const silentIdle = params.get('sso_idle') === '1';
        const silentState = sessionStorage.getItem('cas_atozas_silent');
        if (silentIdle || silentState === 'pending' || silentState === 'done') {
          sessionStorage.setItem('cas_atozas_silent', 'done');
          if (silentIdle) {
            params.delete('sso_idle');
            const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
            window.history.replaceState({}, '', next);
          }
          setLoading(false);
          return;
        }

        sessionStorage.setItem('cas_atozas_silent', 'pending');
        const rawPath = `${window.location.pathname}${window.location.search}`;
        const returnTo = rawPath.startsWith('/login') ? '/home' : (rawPath || '/home');
        window.location.replace(
          `/api/auth/atozas?silent=1&returnTo=${encodeURIComponent(returnTo)}&t=${Date.now()}`
        );
        return;
      }
      setLoading(false);
      return;
    }
    if (skipNextLoadUserRef.current) {
      skipNextLoadUserRef.current = false;
      setLoading(false);
      return;
    }
    try {
      if (getRuntimeConfig().atozasSsoEnabled) {
        const sso = await authAPI.getAtozasMe();
        if (sso?.authenticated !== false && applyAuthSession(sso)) {
          sessionStorage.removeItem('cas_atozas_silent');
          setLoading(false);
          return;
        }
        if (sso?.ssoState === 'expired') {
          localStorage.removeItem('cas_token');
          localStorage.removeItem('cas_user');
          setToken(null);
          setUser(null);
          sessionStorage.setItem('cas_atozas_silent', 'pending');
          window.location.replace(`/api/auth/atozas?silent=1&returnTo=${encodeURIComponent('/home')}&t=${Date.now()}`);
          return;
        }
      }
      const data = await authAPI.getMe();
      setUser(data.user);
    } catch {
      localStorage.removeItem('cas_token');
      localStorage.removeItem('cas_user');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const requestOtp = async (email, purpose = 'login') => authAPI.requestOtp({ email, purpose });

  const verifySignupOtp = async (email, otp) => authAPI.verifySignupOtp({ email, otp });

  const login = async (email, otp) => {
    const data = await authAPI.login({ email, otp });
    localStorage.setItem('cas_token', data.token);
    localStorage.setItem('cas_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, phone, otp) => {
    const data = await authAPI.registerWithOtp({ name, email, phone, otp });
    localStorage.setItem('cas_token', data.token);
    localStorage.setItem('cas_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('cas_show_welcome_splash', '1');
    }
    return data;
  };

  const googleAuth = async (credential) => {
    const data = await authAPI.googleAuth({ credential });
    localStorage.setItem('cas_token', data.token);
    localStorage.setItem('cas_user', JSON.stringify(data.user));
    skipNextLoadUserRef.current = true;
    setUser(data.user);
    setToken(data.token);
    return data;
  };

  const logout = async () => {
    if (getRuntimeConfig().atozasSsoEnabled) {
      try {
        await authAPI.atozasLogout();
      } catch {
        // Local logout still proceeds if the ATOZAS session is already gone.
      }
    }
    localStorage.removeItem('cas_token');
    localStorage.removeItem('cas_user');
    sessionStorage.setItem('cas_atozas_silent', 'done');
    setToken(null);
    setUser(null);
  };

  const updatePreferences = async (prefs) => {
    const data = await authAPI.updatePreferences(prefs);
    setUser((prev) => {
      const nextUser = { ...prev, preferences: data.preferences };
      localStorage.setItem('cas_user', JSON.stringify(nextUser));
      return nextUser;
    });
  };

  const updateProfile = async (profile) => {
    const data = await authAPI.updateProfile(profile);
    setUser(data.user);
    localStorage.setItem('cas_user', JSON.stringify(data.user));
    return data.user;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, updatePreferences, updateProfile,
      requestOtp, verifySignupOtp, googleAuth,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
